<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button, Input, Popover, Tooltip } from '@sveltestrap/sveltestrap';

  import { Clipboard } from '@client/components/clipboard';
  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Switch } from '@client/components/switch';

  import { AssetsService } from '@client/services/assets';

  import { preloadImage } from '@client/utils/images';

  import { formatBytes } from '@common/utils/numbers';

  const { addNotification } = getNotificationsContext();

  export let data = {};
  export let mimeType = '';
  export let invalidFilename = false;

  let currentFilenames = [];
  let files = [];
  let copying = false;

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const onFileUpload = async () => {
    const file = new FormData();
    file.append('file', files[0]);

    const response = await AssetsService.uploadFile(files[0].name, files[0].type, file);

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: data.filename } }), 'error');

      return;
    }

    data.hash = response.hash;
    data.filesize = response.filesize;
    data.filename = response.filename;

    notify($format('label.SUCCESS.ASSET_UPLOAD', { values: { asset: data.filename } }));
  };

  const getMimeType = (type = '') => {
    mimeType = type.substring(type.indexOf('/') + 1);

    if (data.filename.includes('jpg')) {
      mimeType = 'jpg';
    }

    if (data.filename.includes('svg')) {
      mimeType = 'svg';
    }

    return mimeType;
  };

  const copyText = (copyFn) => {
    copying = true;
    copyFn(copyImageUrl);

    setTimeout(() => {
      copying = false;
    }, 1000);
  };

  $: copyImageUrl = data.imageurl + (data.inline ? '?inline=1' : '');

  onMount(async () => {
    getMimeType(data.mimetype);
    const response = await AssetsService.getAllImages();
    currentFilenames = response.map(({ filename }) => filename).filter((filename) => filename !== data.filename);

    if (data.imageurl) {
      preloadImage(data.imageurl);
    }
  });

  $: invalidFilename = currentFilenames.includes(data.filename);
</script>

<div class="image-edit-form">
  <div class="section">
    <div class="heading">
      {$format('label.FILENAME')}
      <Icon id="filename-info" name="help-circle" class="cursor-help h5" />
    </div>
    <Tooltip target="filename-info" placement="top">{$format('label.TOOLTIP.ASSET_IMAGE_FILENAME')}</Tooltip>
    <Input
      type="text"
      class="filename"
      placeholder={$format('label.PLACEHOLDER.INPUT', {
        values: { param: $format('label.FILENAME').toLowerCase() }
      })}
      invalid={!data?.filename?.includes(mimeType) || invalidFilename}
      feedback={!data.filename
        ? $format('label.VALIDATION_TOO_SHORT', { values: { param: $format('label.FILENAME'), length: 1 } })
        : !data.filename.includes(mimeType)
          ? $format('label.ASSET_IMAGE_FILENAME_NO_TYPE_FEEDBACK', { values: { type: mimeType } })
          : $format('label.VALIDATION_NAME_EXISTS', {
              values: {
                type: $format('label.IMAGE'),
                param: $format('label.FILENAME').toLowerCase()
              }
            })}
      bind:value={data.filename}
    />
  </div>
  <div class="section">
    <div class="heading">
      {$format('label.URL')}
      <Icon id="url-info" name="help-circle" class="cursor-help h5" />
    </div>
    <Tooltip target="url-info" placement="top">{$format('label.TOOLTIP.ASSET_IMAGE_URL')}</Tooltip>
    <div class="copy-field">
      <Clipboard text={copyImageUrl} let:copy>
        <Button size="sm" color="secondary" on:click={() => copyText(copy)}>
          {#if copying}
            <Icon name="check" /> {$format('label.COPIED')}
          {:else}
            <Icon name="copy" /> {$format('label.COPY')}
          {/if}
        </Button>
      </Clipboard>
      <Input type="text" class="filename" disabled bind:value={data.imageurl} />
    </div>
  </div>
  <div class="section">
    <div class="heading">
      {$format('label.DETAILS')}
    </div>
    <div class="asset-details">
      <div class="asset-item">
        <div class="image">
          <div class="asset-info">
            <b>{$format('label.PREVIEW')}</b>
            <small
              >{$format('label.DIMENSIONS')}
              {data?.metadata?.imageinfo.width}x{data?.metadata?.imageinfo.height}</small
            >
            <small>{$format('label.FILE_SIZE')} {formatBytes(data.filesize)}</small>
          </div>
          <div class="asset-actions">
            <div class="image-preview">
              <img
                id="popover-image"
                src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                style="background: url({data.imageurl}) no-repeat center center;"
                alt={data.rulename}
              />
              <Popover
                class="image-asset-popover"
                target="popover-image"
                placement="bottom"
                trigger="hover"
                hideOnOutsideClick
              >
                <img class="popover" src={data.imageurl} alt={data.rulename} />
              </Popover>
            </div>
          </div>
        </div>
      </div>
      <div class="asset-item">
        <div class="file">
          <div class="asset-info">
            <b>{$format('label.UPLOAD_FILE')}</b>
            <p>{$format('label.DESCRIPTION.ASSET_IMAGE_UPLOAD_FILE')}</p>
          </div>
          <div class="asset-actions">
            <label class="btn btn-secondary btn-sm" for="document">{$format('label.CHOOSE_FILE')}</label>
            <span id="file-chosen">
              {#if data.filename}
                {data.filename} ({formatBytes(data.filesize)})
              {:else}
                {$format('label.NO_FILE_CHOSEN')}
              {/if}
            </span>
            <input id="document" name="document" hidden type="file" bind:files on:change={onFileUpload} />
          </div>
        </div>
      </div>
      <div class="asset-item">
        <div class="asset-info">
          <b>{$format('label.ASSET_IMAGE_OPTION_INLINE')}</b>
          <p>{$format('label.DESCRIPTION.ASSET_IMAGE_OPTION_INLINE')}</p>
        </div>
        <div class="asset-actions">
          <Switch bind:checked={data.inline} />
        </div>
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  .image-edit-form {
    .section {
      &:first-child {
        margin-top: var(--margin-base);
      }

      + .section {
        margin-top: var(--margin-xlarge);
      }

      .heading {
        font-size: var(--font-size-large);
        font-weight: var(--font-weight-medium);
        margin-bottom: var(--margin-base);
      }
    }


    .copy-field {
      position: relative;

      :global(.btn) {
        position: absolute;
        right: 4px;
        top: 5px;
      }
    }

    :global(.invalid-feedback) {
      color: var(--color-red-500);
      font-size: var(--font-size-medium);
      margin-top: var(--margin-base);
    }

    .asset-details {
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-base);
      padding: var(--padding-small) var(--padding-base);

      .asset-item {
        display: flex;
        padding: var(--padding-large);

        + .asset-item {
          border-top: 1px solid var(--border-color);
        }

        .file,
        .image {
          display: flex;
          flex: 1;
        }
        .asset-info {
          align-items: center;
          flex: 1;

          b {
            color: var(--color-gray-900);
          }

          small {
            color: var(--color-gray-600);
            display: block;
            margin-top: var(--margin-xsmall);
          }
        }

        .asset-actions {
          align-items: center;
          display: flex;
          gap: 10px;

          .image-preview {
            cursor: pointer;

            &:hover {
              opacity: 0.8;
            }

            img {
              background-size: contain !important;
              border: 1px solid var(--border-color);
              border-radius: var(--border-radius-base);
              display: block;
              height: 120px;
              width: auto;
            }
          }
        }
      }
    }
  }

  :global(.image-asset-popover) {
    max-width: none;
  }

  @media only screen and (max-width: 1200px) {
    :global(.image-asset-popover) {
      max-width: 90%;
    }
  }

  @media only screen and (max-width: 650px) {
    .file {
      flex-direction: column;

      .asset-actions {
        flex-direction: column;
        margin-top: var(--margin-large);

        label {
          width: 100%;
        }
      }
    }
  }

  @media only screen and (max-width: 420px) {
    .image-edit-form {
      .asset-details {
        .asset-item {
          display: block;

          .image {
            display: block;
          }

          .asset-actions {
            margin-top: var(--margin-large);
          }
        }
      }
    }
  }
</style>
